import type { Address } from "proto/address";
import type { Vertical } from "$lib/def/verticals";
import { profile$ } from "$lib/serv/core/auth";
import { load, promiseGeoAddress, promisePlaceAddress } from "$lib/serv/vendor-integration/google-maps";
import { observe, setItem } from "$lib/serv/core/local-store";
import { BehaviorSubject, catchError, combineLatest, EMPTY, firstValueFrom, from, map, merge, Observable, of, shareReplay, Subject, switchMap, take, tap, type ObservedValueOf, combineLatestWith, throttleTime } from "rxjs";
// import type { Page } from "@sveltejs/kit";
import type { User } from "proto/user";

const KEY = 'locations';

export let _lastGeolocation: GeolocationPosition | undefined;

//#############################################################
// Auto fill location with recent, saved and geocoded addresses
//#############################################################

export function createLocationAutofill($:Observable<Record<string, string> | null>) {
	return $.pipe(
		// let first grab the stored (recent) locations so we can match any existing location and fill missing props (is this even possible)
		switchMap(loc => locations$.pipe(map(list => {
			if (!loc) return null;
			if (list) {
				const idx = findLocationIndex(list, loc);
				if (~idx) loc = Object.assign({...list[idx]}, loc); // argument properties take preference over stored location properties
			}
			return loc;
		}))),
		// use logged in user's saved addresses
		switchMap(loc => profile$.pipe(map(user => {
			if (!loc) return null;
			const list = user?.saved_addresses ?? [];
			const idx = findLocationIndex(list, loc);
			if (~idx) loc = Object.assign({...list[idx]}, loc); // argument properties take preference over stored location properties
			return loc;
		}))),
		// use possible Google Maps place id
		switchMap(loc => {
			if ((loc?.latitude && loc.longitude) || !loc?.place_id) return of(loc);
			return from(promisePlaceAddress(loc.place_id)).pipe(
				map(results => {
					const { address_components, geometry } = results[0];
					return {
						...loc,
						...dissectGooglePlaceAddress(address_components),
						latitude: geometry.location.lat().toString(),
						longitude: geometry.location.lng().toString(),
					};
				}),
			);
		}),
		map(loc => ({
			...loc,
			hasGeo: (!!loc?.latitude && !!loc?.longitude),
			deliverable: !!loc?.town && !!loc?.street_name && !!loc?.street_number,
		} as LandingItemsLocation))
		// todo: add distinctUntilChanged
	);
}

export type LandingItemsLocation = Partial<Address> & {
	/**
	 * 'groc': Grocery vertical
	 * undefined: Restaurant vertical
	 */
	vertical?: Vertical;

	/**
	 * Part of a grocery location is the customer's selected store.
	 */
	store?: string;

	/**
	 * 'restaurant' or 'store' based on search pills
	 */
	search_vertical?: Vertical;

	/**
	 * This is how, in the template, we'll switch between address search and Landing Items.
	 */
	hasGeo?: boolean;

	/**
	 * This is how the app knows if a cart can be initialised. We need at least a street
	 * number, street name and town.
	 */
	deliverable?: boolean;
}

// ############################################################
// LANDING ITEMS PARAMETERS
// ############################################################

const subjLandingItemsParams = new BehaviorSubject<Record<string, string> | null>(null);

/**
 * To observe the last location of the home (root/landing items) route. Mostly used to
 * retrieve landing items data. Usefull for constructing hrefs back to the home route.
 */
export const landingItemsParams$ = createLocationAutofill(subjLandingItemsParams).pipe(shareReplay(1));

/**
 * Only call this on the root route where `landingItemsParams$` is subscribed to. If
 * called without location parameters `landingItemsParams$` will emit resulting in
 * dependant subscribers reloading their locaiton dependant data.
 * @param value The current page URL query parameters relevant to a location
 */
export function setLandingItemsParams(value: Record<string, string> | null) {
	subjLandingItemsParams.next(value);
}

// ############################################################
// BROWSING LOCATION
// ############################################################

const subjBrowsingLocation = new BehaviorSubject<Record<string, string> | null>(null);

/**
 * Observe the current route location. Useful for determining if the current page content
 * should contain location relevant information.
 */
export const browsingLocation$ = createLocationAutofill(subjBrowsingLocation).pipe(shareReplay(1));

/**
 * Call this on the every route (root layout). This is a realtime update of the current
 * location being browsed.
 * 
 * If no location is being browsed an entity address can also be relevant. For example if
 * you are viewing an active order the delivery address is relevant.
 * 
 * @param value The current page URL query parameters relevant to a location
 */
export function setBrowsingLocation(value: Record<string, string> | null) {
	subjBrowsingLocation.next(value);
}

// ############################################################
// OPTIMISTIC LOCATION
// ############################################################

/**
 * The purpose of this stream is to provide a +page and its components a location to use
 * for location dependant links and possibly data. For example navigating back home after
 * viewing a profile page or setting links to the home page from a profile page.
 * 
 * The location is determined by the following priority:
 * 1. URL location or last Landing Items location.
 * 2. User's single saved address (ignored if user has mulitple saved addresses)
 * 3. User's last ordered location.
 * 4. Closest saved address to user's current position if location permission is granted.
 * 5. User's current position converted to an address if location permission is granted.
 * 
 * @param isRootRoute Is the stream used on the root route or not. Typical input would be
 * `fromStore(page).pipe(map(p => p.route.id === '/(app)'))`.
 * @param profile$ The user profile observalbe. Used to get saved addresses and last
 * ordered location.
 * @returns An object with geolocation and address properties.
 */
export function streamOptimisticLocation(
	routeId:Observable<string|null>,
	profile$:Observable<User | null>,
	ignoreNoneURLLocations = false
) {
	return routeId.pipe(
		map(id => id === '/(app)'),
		combineLatestWith(landingItemsParams$, browsingLocation$),
		switchMap(([isHome, li, brw]) => {
			// const isRoot = page.route.id === '/(app)';
			// on the home route LI location takes preference over browsing location
			return of(isHome ? (li?.hasGeo && li || brw) : (brw?.hasGeo && brw || li));
		}),
		switchMap(loc => {
			if (loc.hasGeo || ignoreNoneURLLocations) return of(loc);
			return profile$.pipe(
				// if we have no location we'll grab the user's last ordered location or single saved address
				map(user => {
					if (user?.saved_addresses?.length === 1) return user?.saved_addresses[0] as Partial<Address>;
					if (user?.last_ordered_location) {
						if (user?.saved_addresses?.length) {
							const lastLocation = user?.saved_addresses.find(addr => addr.label === user?.last_ordered_location?.saved_address_label);
							if (lastLocation) return lastLocation;
						}
					}
				}),
			);
		}),
		switchMap(loc => {
			if (loc || ignoreNoneURLLocations) return of(loc);
			// if the user allowed it use the current position and convert it to an address
			return from(obsCurrentPosition()).pipe(
				catchError(er => of(null)), // if we can't get the user's current position we'll just return null
				switchMap(pos => pos ? profile$.pipe(map(user => ({ pos, user }))) : of({ pos, user: null })),
				// if we have a user we'll try find the closest saved address
				switchMap(({pos, user}) => {
					if (!pos) return of(null);
					let lastDistance = Number.MAX_SAFE_INTEGER;
					let lastLocation: Partial<Address> | undefined;
					if (user?.saved_addresses?.length) {
						user?.saved_addresses.forEach(addr => {
							const distance = getGeoDistance(pos.coords, addr);
							if (distance < lastDistance) {
								lastDistance = distance;
								lastLocation = addr;
							}
						});
						if (lastLocation) return of(lastLocation);
					}
					// one last try, we'll convert the user's current position to an address
					return from(load()).pipe(
						switchMap(() => from(promiseGeoAddress(pos.coords.latitude, pos.coords.longitude))),
						// tap(res => console.log('current address', res)),
						map(results => {
							if (!results.length) return null;
							const result = results.find(r => r.types.includes('street_address'));
							if (!result) return null;
							return {
								...dissectGooglePlaceAddress(result.address_components),
								latitude: result.geometry.location.lat().toString(),
								longitude: result.geometry.location.lng().toString(),
							} as Partial<Address>;
						}),
					);
				}),
			);
		}),
		map(loc => loc && ({
			...loc,
			hasGeo: (!!loc?.latitude && !!loc?.longitude),
			deliverable: !!loc?.town && !!loc?.street_name && !!loc?.street_number,
		} as LandingItemsLocation))
	)
}

// ############################################################
// LOCATIONS
// ############################################################

/**
 * When dealing with partial locations we often need to find a complete location to give
 * the user a better experience. This function matches an incomplete location to a
 * complete location in an array of trusted locations.
 * @param locations An array of of complete locations. Locations from Google places are
 * concidered complete. They have at least a geolocation and suburb.
 * @param value A possibly incomplete location we want to find a complete location for.
 * @returns The index of a complete location in the locations array. If no complete
 * location is found -1 is returned.
 */
export function findLocationIndex(locations: Location[], value?: Location) {
	if (!locations?.length || !value) return -1;
	return locations.findIndex(location => {
		if (value.latitude || value.longitude) {
			return location.latitude === value.latitude && location.longitude === value.longitude;
		} else if (value.place_id) {
			return location.place_id === value.place_id;
		} else if (value.location_id) {
			return location.location_id === value.location_id;
		} else if (value.label) {
			return location.label === value.label;
		}
		return false;
	});
}

export const locations$ = observe<Location[]>(KEY).pipe(map(i => i.value));

/**
 * Store an address in local storage. If the address already exists it will be updated.
 * @param value Location to store
 */
export async function addLocation(value:Partial<Location>) {
	const storageLocations = await firstValueFrom(locations$);
	const locations = storageLocations ?? [];
	const idx = locations ? findLocationIndex(locations, value) : -1;
	let location: Partial<Location>;
	if (~idx) {
		location = locations.splice(idx,1)[0];
		Object.assign(location, value);
	} else {
		location = value;
	}
	locations.push(value);
	setItem(KEY, locations);
}

// ############################################################
// UTILS
// ############################################################

export const dissectGooglePlaceAddress = (comps: { long_name: string, short_name: string, types: string[] }[]):Partial<Location> => {
	const values = [
		{ type: 'street_number', name: 'street_number', prop: 'long_name' },
		{ type: 'route', name: 'street_name', prop: 'long_name' },
		{ type: 'sublocality', name: 'suburb', prop: 'long_name' },
		{ type: 'locality', name: 'town', prop: 'long_name' },
		{ type: 'administrative_area_level_1', name: 'province', prop: 'long_name' },
		{ type: 'postal_code', name: 'postal_code', prop: 'long_name' },
	] as { type:string, name:keyof Location, prop:'long_name'|'short_name' }[];
	return values.reduce<any>((location, value) => {
		const comp = comps.find(comp => comp.types.includes(value.type));
		if (comp) location[value.name] = comp[value.prop];
		return location;
	}, {});
};

/**
 * Converts an Address to a query string.
 * Consider using `parseObjectValuesToString` instead.
 * @param params Address object
 * @param keepLongGeoNames If false 'lat' and 'lon' are used instead of 'latitude' and 'longitude'
 * @returns A query string without the leading '?' character.
 */
export function toQueryString(params?: Location, keepLongGeoNames?: boolean) {
	if (!params) return '';
	const arQuery = [
		[keepLongGeoNames ? 'latitude' : 'lat', params.latitude ?? ''],
		[keepLongGeoNames ? 'longitude' : 'lon', params.longitude ?? ''],
	];
	const propsAddr: (keyof typeof params)[] = [
		'location_id',
		'street_number', // string - which should never be empty
		'street_name',
		'suburb',
		'town',
		'postal_code',
	];
	propsAddr.forEach(prop => {
		if (!params[prop]) return; // we don't want empty strings, zero, false, undefined or null
		arQuery.push([prop, params[prop]?.toString() ?? '']);
	});
	return arQuery.map(([k,v]) => `${k}=${v}`).join('&');
}

export interface CurrentPositionProgress {
	busy: boolean;
	pos?: GeolocationPosition;
	geocode?: Awaited<ReturnType<typeof promiseGeoAddress>>
	error?: string;
}

// export function findCurrentAddresses() {
// 	const noPermission$ = of({ busy: false, error: "permission" } as CurrentPositionProgress);
// 	const failedGeocoder$ = of({ busy: false, error: "geocoder" } as CurrentPositionProgress);
// 	const obsGeoAddr = (pos: GeolocationPosition) => from(promiseGeoAddress(pos.coords.latitude, pos.coords.longitude));
// 	return merge<CurrentPositionProgress[]>(
// 		of({ busy: true }),
// 		permit('geolocation').pipe(
// 			switchMap(state => state === 'denied' ? of(undefined) : obsCurrentPosition()),
// 			map(pos => ({ busy: !!pos, pos, error: pos ? undefined : "permission" })),
// 		),
// 	).pipe(
// 		switchMap(val => val.error ? noPermission$ : merge<CurrentPositionProgress[]>(
// 			of(val),
// 			!val.pos ? EMPTY : obsGeoAddr(val.pos).pipe(
// 				map(geocode => ({ ...val, busy:false, geocode })),
// 			),
// 		)),
// 		catchError(er => {
// 			return failedGeocoder$;
// 		}),
// 	)
// }

/**
 * For UI to know if the user has opted in for geolocation. They click any "use my
 * location" button.
 */
export const geoOpted$ = observe<boolean>('geo-opted').pipe(map(i => i.value));
export function setGeoOpted(value = true) {
	setItem('geo-opted', value);
}

const position$ = new Observable<GeolocationPosition>(subscriber => {
	const id = navigator.geolocation.watchPosition(subscriber.next.bind(subscriber));
	return () => navigator.geolocation.clearWatch(id);
});

export function streamCurrentAddresses(interval = 5000) {
	return position$.pipe(
		throttleTime(interval),
		switchMap(pos => from(promiseGeoAddress(pos.coords.latitude, pos.coords.longitude))),
		map(results => prettyGeoResults(results)),
	)
}

function prettyGeoResults(results:google.maps.GeocoderResponse['results']) {
	return results.map(({ types, address_components, geometry, formatted_address }) => {
		if (!types.includes('street_address')) return null; // || geometry.location_type !== 'ROOFTOP'
		return {
			address: {
				...dissectGooglePlaceAddress(address_components),
				latitude: geometry.location.lat().toString(),
				longitude: geometry.location.lng().toString(),
			},
			description: formatted_address
		};
	}).filter(Boolean) as {address:Partial<Location>,description:string}[]; // remove `null`s
}

async function promiseCurrentAddresses(pos?: GeolocationPosition) {
	if (!pos) pos = await firstValueFrom(obsCurrentPosition());
	const res = await promiseGeoAddress(pos.coords.latitude, pos.coords.longitude);
	return res;
}

function obsCurrentPosition() {
	return new Observable<GeolocationPosition>(subscriber => {
		navigator.geolocation.getCurrentPosition(pos => {
			subscriber.next(pos);
			subscriber.complete();
		}, er => {
			subscriber.error(er);
		}, { enableHighAccuracy: true, timeout: 10000, maximumAge:0 })
	})
}

export type Location = Partial<Address> & {
	
	/**
	 * If no geolocation exists a Google place Id may provide it
	 */
	place_id?: string; // google maps api

}

export const LOCATION_PROPS:(keyof Address)[] = [
	'latitude',
	'longitude',
	'street_name',
	'street_number',
	'location_id',
	'label',
	// 'place_id', // not needed in the URL location query params
	'suburb',
	'town',
	'building_type',
	'complex',
	'province',
	'postal_code',
];

const checkRange$ = new BehaviorSubject(false)
export function setIfInRange(inRange: boolean) {
	checkRange$.next(inRange)
}
export function getRange() {
	return checkRange$
}

//#############################################################
// GEO HELPER FUNCTIONS
//#############################################################

/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param a First coordinate
 * @param b Second coordinate
 * @returns Distance in KM
 */
export function getGeoDistance(
	a: {latitude:number|string, longitude:number|string},
	b: {latitude:number|string, longitude:number|string}
) {
  const earthRadiusKm = 6371; // Earth's radius in kilometers (between equatorial and polar radii, but more equatorial)
	const degToRad = (deg: number|string) => (+deg * Math.PI) / 180; // Degree to radian conversion

  // Convert latitude and longitude from degrees to radians
  const latA = degToRad(a.latitude);
  const lonA = degToRad(a.longitude);
  const latB = degToRad(b.latitude);
  const lonB = degToRad(b.longitude);

  // Calculate differences between coordinates
  const deltaLat = latB - latA;
  const deltaLon = lonB - lonA;

  // Haversine formula
  const x =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));

  // Calculate the distance
  const distance = earthRadiusKm * c;

  return distance;
}

// takes a google address description and removes the country and postal code
export const removeCountryAndPostalCode = (address: string): string =>
  address.replace(", South Africa", "").replace(/, ([0-9]{4})/, "");
